import {
  Box,
  FieldSelect,
  FieldText,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import noop from 'lodash/noop';
import { useCallback } from 'react';
import { connect } from 'react-redux';
import {
  Field,
  Form,
  FormErrors,
  InjectedFormProps,
  reduxForm,
} from 'redux-form';

import type { RootReducerStateT } from 'app/reducers/rootReducer';
import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import {
  getVariableNameErrorMessage,
  isValidVariableName,
} from 'components/powerFlowShared/helpers/flowVariableHelpers';
import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import { FieldAnnouncementPicker } from 'components/shared/announcementPicker';
import { AdapterTypes } from 'components/shared/announcementPicker/adapters';
import { AnnouncementItem } from 'components/shared/announcementPicker/adapters/announcementAdapter';
import { getRequestID } from 'components/shared/util/request';
import type { FlowManagerEditModalComponentT } from 'fable/flowEditor';

import type { FlowCallNodeDialInput } from '../nodeTypes';

import styles from './dialInput.module.scss';

const ANNOUNCEMENT_FIELD = 'announcement';
const MAX_DIGITS_FIELD = 'max_digits';
const END_KEY_FIELD = 'end_key';
const VARIABLE_ID_FIELD = 'variable_id';

type StateProps = ReturnType<typeof mapStateToProps>;

type OwnProps = FlowManagerEditModalComponentT<FlowCallNodeDialInput>;

type Props = OwnProps & StateProps;

type InjectedProps = InjectedFormProps<FormValues, Props>;

type FormValues = {
  [ANNOUNCEMENT_FIELD]: AnnouncementItem;
  [END_KEY_FIELD]: null | '*' | '#';
  [MAX_DIGITS_FIELD]: null | number;
  [VARIABLE_ID_FIELD]: string;
};

type Errors = {
  [Key in
    | typeof ANNOUNCEMENT_FIELD
    | typeof END_KEY_FIELD
    | typeof MAX_DIGITS_FIELD
    | typeof VARIABLE_ID_FIELD]?: string;
};

const validate = (
  { announcement, end_key, variable_id, max_digits }: Partial<FormValues>,
  { initialValues, scopeVars }: Props,
): FormErrors => {
  const errors: Errors = {};

  if (!announcement) {
    errors[ANNOUNCEMENT_FIELD] = 'Please choose an announcement';
  }

  if (!max_digits && !end_key) {
    errors[MAX_DIGITS_FIELD] =
      'Please fill either max input length or pick an end key';
    errors[END_KEY_FIELD] =
      'Please fill either max input length or pick an end key';
  }

  if (!variable_id) {
    errors[VARIABLE_ID_FIELD] = 'Please fill variable name';
  } else if (
    variable_id !== initialValues.variable_id &&
    scopeVars.existing.indexOf(variable_id) !== -1
  ) {
    errors[VARIABLE_ID_FIELD] = 'Variable name is already in use';
  } else if (!isValidVariableName(variable_id)) {
    errors[VARIABLE_ID_FIELD] = getVariableNameErrorMessage(variable_id) || '';
  }

  return errors;
};

function DialInputImpl({
  handleSubmit,
  node,
  nodeLabel,
  replaceNode,
  triggerClose,
  variableIDLocked,
  dirty: isChanged,
}: Props & InjectedProps): JSX.Element {
  const END_KEYS = [
    { id: '', name: 'None' },
    { id: '#', name: '#' },
    { id: '*', name: '*' },
  ];

  const handleSaveClick = useCallback(
    ({ announcement, max_digits, end_key, variable_id }: FormValues) => {
      const newNode: FlowCallNodeDialInput = {
        ...node,
        icon: 'i-phone',
        isAction: false,
        isEditable: true,
        isRemovable: true,
        nodeType: FlowNodeTypes.DIAL_INPUT,
        overlay: [],
        payload: {
          next_node_id: node.payload?.next_node_id || getRequestID(),
          announcement: {
            file: {
              ...announcement,
              filename: announcement.label,
            },
          },
          max_digits,
          end_key,
          variable_id,
        },
        text: 'Phone input',
      };

      replaceNode(newNode);
      triggerClose();
    },
    [node, replaceNode, triggerClose],
  );

  return (
    <Box width="600px">
      <ModalHead title={nodeLabel} onClose={triggerClose}>
        <Text.Paragraph subdued>
          Allow customers to give input with their dial pad during a call (e.g.
          a PIN, ZIP code, or phone number).
        </Text.Paragraph>
      </ModalHead>
      <Form onSubmit={handleSubmit(handleSaveClick)}>
        <ModalBody>
          <Field
            adapter={AdapterTypes.Announcement}
            className={styles.field}
            component={FieldAnnouncementPicker}
            name={ANNOUNCEMENT_FIELD}
            onSelect={noop}
          />
          <Text.Heading className={styles.textHeading} variant="heading-3">
            Input condition
          </Text.Heading>
          <Text.Paragraph className={styles.paragraph}>
            Set a max number of key presses or an end key. You can also set both
            conditions
          </Text.Paragraph>
          <Field
            className={styles.field}
            component={FieldText}
            label="Max input length"
            min="0"
            name={MAX_DIGITS_FIELD}
            parse={(val: string) => parseInt(val)}
            placeholder="-"
            type="number"
          />
          <Field
            className={styles.field}
            component={FieldSelect}
            items={END_KEYS}
            label="End key"
            name={END_KEY_FIELD}
            selectedItems={[END_KEYS[0]]}
          />
          <Field
            component={FieldText}
            disabled={variableIDLocked}
            label="Input variable name"
            name={VARIABLE_ID_FIELD}
            placeholder="Type variable name"
          />
        </ModalBody>
        <ModalFoot
          cancelOnClick={triggerClose}
          confirmName={getConfirmButtonVerb(node)}
          confirmProps={{ disabled: !isChanged }}
          confirmOnClick={handleSubmit(handleSaveClick)}
        />
      </Form>
    </Box>
  );
}

const mapStateToProps = (
  _state: RootReducerStateT,
  { node: { payload }, scopeVars }: OwnProps,
) => {
  const { announcement } = payload || {};

  const initialValues = {
    ...payload,
    announcement: announcement && {
      author: announcement.file.author ?? '',
      url: announcement.file.url ?? '',
      label: announcement.file.filename ?? '',
    },
  };

  return {
    initialValues,
    variableIDLocked: payload?.variable_id
      ? scopeVars.descendantNeeds.indexOf(payload.variable_id) !== -1
      : false,
  };
};

export const DialInput = connect(mapStateToProps)(
  reduxForm<FormValues, Props>({
    enableReinitialize: true,
    form: 'callFlowDialInputForm',
    validate,
  })(DialInputImpl),
);
